import { useState } from "react";

import { CHAT_TYPES } from "@constants/chat";
import { CircularProgress, Modal, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";

import Button from "@components/Button";
import { deleteConversation } from "@components/Conversation/service";

import GatherlanceTypography from "@pages/gatherlance-landing/components/GatherlanceTypography";

import { ReactComponent as GawainIcon } from "@assets/icons/gawain_ai.svg";
import { ReactComponent as TrashIcon } from "@assets/icons/trash.svg";

import { Conversation } from "./type";

interface Props {
  data: Partial<Conversation>[];
  loading?: boolean;
  onClick?: (id: string) => void;
}

export const Conversations = ({ data, onClick, loading }: Props) => {
  const {
    palette: { common },
  } = useTheme();

  const [showModal, setShowModal] = useState<string>();
  const [loadingDelete, setLoadingDelete] = useState(false);

  const renderContent = (content: Conversation["title"]) => (
    <GatherlanceTypography
      overflow={"hidden"}
      maxHeight={"75px"}
      dangerouslySetInnerHTML={{
        __html: content ? content : "New chat",
      }}
    />
  );

  const handleDelete = async (id: string) => {
    setLoadingDelete(true);
    const isSuccess = await deleteConversation(id, CHAT_TYPES.ASSISTANT);
    isSuccess && setShowModal(null);
    setLoadingDelete(false);
  };

  return (
    <Stack gap={"12px"}>
      {loading && (
        <Stack alignItems={"center"}>
          <CircularProgress />
        </Stack>
      )}
      {!!data?.length &&
        data.map((item) => (
          <Stack
            key={item.id}
            border={common.border}
            borderRadius={"8px"}
            p={"14px"}
            gap={"16px"}
            onClick={() => onClick?.(item.id)}
            sx={{
              cursor: "pointer",
              ":hover": {
                bgcolor: "#f8f8f85e",
                transition: "all 0.2s ease-in-out",
              },
            }}
          >
            {renderContent(item.title)}
            <Stack
              flexDirection={"row"}
              gap={1}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Stack flexDirection={"row"} gap={"10px"} alignItems={"center"}>
                <GawainIcon cursor={"pointer"} width={35} height={35} />
                <Typography fontSize={"12px"}>
                  {dayjs(item.session).format("DD MMM YYYY")}
                </Typography>
              </Stack>
              <TrashIcon
                cursor={"pointer"}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal(item.id);
                }}
              />
            </Stack>
          </Stack>
        ))}

      <Modal
        open={!!showModal}
        onClose={() => setShowModal(null)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          bgcolor={"white"}
          borderRadius={2}
          width={{ xs: "90%", sm: 400 }}
          p={4}
          gap={2}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Typography textAlign={"center"}>
            Are you sure you want to delete this conversation?
          </Typography>
          <Stack gap={2} flexDirection={"row"}>
            <Button variant={"text"} onClick={() => setShowModal(null)}>
              Cancel
            </Button>
            <Button onClick={() => handleDelete(showModal)} loading={loadingDelete}>
              OK
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </Stack>
  );
};
